import {
  OmakasePolymorphicComponent,
  OmakasePolymorphicComponentProps,
} from 'omakase/utils/OmakasePolymorphicComponent';
import { PadButton, PadButtonProps, PadButtonTypeGuard } from 'omakase';
import { PadButtonDefaultElement } from 'omakase/PadButton/PadButtonCore';
import { handleBlurFormField, handleChangeFormField } from '@tock/shared/ui/forms/formHelpers';
import { useField } from 'formik';
import React from 'react';
import _ from 'lodash';

type FormPadButtonRadioProps = Omit<PadButtonProps, 'checked' | 'type'> & {
  component?: React.ReactNode;
};

/**
 * (Formik) implementation of a Pad Button
 */
const FormPadButtonRadio: OmakasePolymorphicComponent<
  FormPadButtonRadioProps,
  typeof PadButtonDefaultElement,
  PadButtonTypeGuard
> = React.forwardRef(
  <E extends React.ElementType = typeof PadButtonDefaultElement>(
    {
      component,
      name,
      value,
      ...rest
    }: OmakasePolymorphicComponentProps<FormPadButtonRadioProps, E, PadButtonTypeGuard>,
    ref: React.Ref<HTMLInputElement> | undefined
  ) => {
    const [fieldConfig] = useField(name);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
      handleChangeFormField(
        fieldConfig,
        event,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rest.onChange as (evt: React.ChangeEvent<any>) => void
      );

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) =>
      handleBlurFormField(fieldConfig, event, rest.onBlur);

    const Component: React.ElementType = component || PadButtonDefaultElement;

    // Destructuring out onChange, onBlur, and value to prevent them from being declared twice on PadButton
    const trimmedFieldConfig = _.omit(fieldConfig, ['onChange', 'onBlur', 'value']);

    return (
      <PadButton
        onChange={handleChange}
        onBlur={handleBlur}
        type="radio"
        component={Component}
        ref={ref}
        value={value}
        checked={fieldConfig.value === value}
        {...trimmedFieldConfig}
        {...rest}
      />
    );
  }
);

export default FormPadButtonRadio;
